import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { motion } from 'framer-motion';
import { X, ZoomIn, Check } from 'lucide-react';

// Produce a cropped square JPEG blob from the source image + crop pixels
async function getCroppedBlob(imageSrc, cropPixels) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    size,
    size
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
  });
}

export default function ImageCropModal({ src, onCancel, onSave, saving }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    const blob = await getCroppedBlob(src, croppedAreaPixels);
    const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
    onSave(file);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="image-crop-modal"
      >
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-xl font-bold gradient-text pb-0.5">Adjust your photo</h2>
          <button
            onClick={onCancel}
            className="w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
            data-testid="crop-cancel-x"
          >
            <X size={18} className="text-slate-600" />
          </button>
        </div>

        <div className="relative w-full h-72 bg-slate-900">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-slate-500 text-center">Drag to move, pinch or use the slider to zoom</p>
          <div className="flex items-center gap-3">
            <ZoomIn size={18} className="text-slate-400 flex-shrink-0" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full accent-emerald-500 cursor-pointer"
              data-testid="crop-zoom-slider"
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button
              onClick={onCancel}
              className="btn-3d btn-3d-outline flex-1 h-12 rounded-2xl font-semibold"
              data-testid="crop-cancel-button"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-3d btn-3d-accent flex-1 h-12 rounded-2xl font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="crop-save-button"
            >
              <Check size={18} />
              {saving ? 'Saving...' : 'Save photo'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
